import { useContentfulImages } from '@snapchat/mw-contentful-client';
import type { Tile } from '@snapchat/mw-contentful-schema';
import {
  EditorialGalleryCard as EditorialGalleryCardSDS,
  MessageContext,
} from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useContext } from 'react';

import { AppContext } from '../../AppContext';
import {
  defaultImageAltText,
  editorialGalleryDateOptions,
  getGalleryImgSrcSetSizes,
  globalDefaultImage,
} from '../../constants/gallery';
import { useFormatDate } from '../../hooks/useFormatDate';
import { UserAction } from '../../types/events';
import { combineImageSources } from '../../utils/combineImageSources';
import { getContentfulInspectorProps } from '../../utils/contentful/getContentfulInspectorProps';
import { parseMedia } from '../../utils/media';
import { renderRichTextMarkingsOnly } from '../../utils/renderText/renderRichText';
import { ConsumerContext } from '../ConsumerContextProvider';
import { imageQuality } from '../Image';
import type { GalleryTileProps } from '../Tile/query';

export const EditorialGalleryCard: FC<Omit<GalleryTileProps, 'linkedFrom'>> = ({
  analytics,
  date,
  label,
  media,
  slugReference,
  sys,
  title,
  url,
  brand,
}) => {
  const { formatDate } = useFormatDate();
  const { logEvent } = useContext(ConsumerContext);
  const { getImageSources } = useContentfulImages();
  const { isRTL } = useContext(AppContext);
  const { formatMessage } = useContext(MessageContext);

  const validUrl = url ?? (slugReference?.slug ? `/${slugReference.slug}` : undefined);

  const logEventAction = () => {
    validUrl &&
      logEvent?.({
        component: 'Editorial Gallery Card',
        type: UserAction.Click,
        label: analytics?.label ? `${analytics.label} - ${validUrl}` : validUrl,
        url: validUrl,
      });
  };

  let { imageSource, imageAltText } = parseMedia(media?.media);
  const { imageSource: mobileImageSource } = parseMedia(media?.mobileMedia);

  if (!imageSource) {
    imageSource = globalDefaultImage;
    imageAltText = defaultImageAltText;
  }

  const quality = imageQuality[media?.quality ?? 'Standard'];
  const imageSrcSettings = { size: getGalleryImgSrcSetSizes(quality) };
  const imgSrcs = combineImageSources({
    desktop: getImageSources(imageSource, imageSrcSettings),
    mobile: getImageSources(mobileImageSource, imageSrcSettings),
  });

  const {
    contentfulDescriptionDataset,
    brandDataset,
    titleDataset,
    labelDataset,
    mediaDataset,
    dateDataset,
  } = getContentfulInspectorProps<Tile>({
    entryId: sys.id,
    fieldIds: ['title', 'brand', 'label', 'date', 'media', 'contentfulDescription'],
  });

  return (
    <EditorialGalleryCardSDS
      key={sys.id}
      brand={brand}
      title={renderRichTextMarkingsOnly(title)}
      description={renderRichTextMarkingsOnly(label)}
      dateTime={date ? new Date(date) : undefined}
      date={formatDate(date, editorialGalleryDateOptions).replaceAll('/', '.')}
      onClick={logEventAction}
      imgSrcs={imgSrcs}
      imgAltText={imageAltText}
      link={validUrl}
      ctaLabel={formatMessage({ id: 'editorial-gallery-cta-label', defaultMessage: 'Read More' })}
      isRtl={isRTL}
      dataset={contentfulDescriptionDataset}
      brandDataset={brandDataset}
      titleDataset={titleDataset}
      descriptionDataset={labelDataset}
      mediaDataset={mediaDataset}
      dateDataset={dateDataset}
    />
  );
};

EditorialGalleryCard.displayName = 'EditorialGalleryCard';
